// ═══════════════════════════════════════════════════════════════════════════
// COIN METADATA UTILITY
// Decimals resolution order:
//   1. KNOWN_DECIMALS (instant, compile-time verified)
//   2. Supabase coin_metadata cache (fast, ~50ms for all coins in one query)
//   3. RPC cache (session memory, populated by background verify)
//   4. RPC fetch (fallback for brand new unknown tokens)
//   5. Default 9
//
// Background verify runs AFTER display — silently corrects any wrong values
// and writes them back to Supabase so future loads are instant and correct.
// ═══════════════════════════════════════════════════════════════════════════

import { getCachedIcon } from './geckoApi';
import { getDecimalsFromSupabase, upsertCoinMetadata } from './supabaseManager';

// ─── Known decimals — VERIFIED on-chain via Suiscan ──────────────────────
// Only add here after confirming decimals on Suiscan or equivalent explorer.
// Background verify will catch any mistakes and self-heal via Supabase.
const KNOWN_DECIMALS = {
  // Stablecoins (6)
  '0x5d4b302506645c37ff133b98c4b50a5ae14841659738d6d733d59d0d217a93bf': 6, // USDC (Wormhole)
  '0xdba34672e30cb065b1f93e3ab55318768fd6fef66c15942c9f7cb846e2f900e7': 6, // USDC (native)
  '0xc060006111016b8a020ad5b33834984a437aaa7d3c74c18e09a95d48aceab08c': 6, // USDT (Wormhole)
  '0x375f70cf2ae4c00bf37117d0c85a2c71545e6ee05c4a5c7d282cd66a4504b068': 6, // USDT (native)
  '0x97c7571f4406cdd7a95f3027075ab80d3e9c937c2a567690d31e14ab1872ccee': 6, // XMN (xMoney) ✓
  '0xc5b61b1e1f7f88511c9c0c6f475f823c66cc4e2d39a49beb6777059710be8404': 6, // TOILET ✓ verified Suiscan

  // 8-decimal tokens
  '0xaf8cd5edc19c4512f4259f0bee101a40d41ebed738ade5874359610ef8eeced5': 8, // WBTC (Wormhole)

  // 9-decimal tokens
  '0x0000000000000000000000000000000000000000000000000000000000000002': 9, // SUI ✓
  '0x06864a6f921804860930db6ddbe2e16acdf8504495ea7481637a1c8b9a8fe54b': 9, // CETUS ✓
  '0x356a26eb9e012a68958082340d4c4116e7f55615cf27affcff209cf0ae544f59': 9, // WAL ✓
  '0xdeeb7a4662eec9f2f3def03fb937a663dddaa2e215b8078a284d026b7946c270': 9, // DEEP ✓
  '0xbde4ba4c2e274a60ce15c1cfff9e5c42e41654ac8b6d906a57efa4bd3c29f47d': 9, // haSUI ✓
  '0xd1b72982e40348d069bb1ff701e634c117bb5f741f44dff91e472d3b01461e55': 9, // stSUI ✓
  '0x5145494a5f5100e645e4b0aa950fa6b68f614e8c59e17bc5ded3495123a79178': 9, // NS ✓
};

// ─── Known icons — tokens that skip RPC need hardcoded icons ─────────────
// Add an entry here for any token in KNOWN_DECIMALS that has a well-known icon.
export const KNOWN_ICONS = {
  '0x0000000000000000000000000000000000000000000000000000000000000002':
    'https://cdn.jsdelivr.net/gh/simplr-sh/coin-logos/images/sui/standard.png',        // SUI
  '0x356a26eb9e012a68958082340d4c4116e7f55615cf27affcff209cf0ae544f59':
    'https://cdn.jsdelivr.net/gh/simplr-sh/coin-logos/images/walrus-xyz/standard.png', // WAL
};

// ─── Session caches ───────────────────────────────────────────────────────
const supabaseDecimalsCache = {}; // { coinType: { decimals, iconUrl } } — from Supabase
const rpcDecimalsCache = {};      // { coinType: decimals } — from RPC
const rpcIconCache = {};          // { coinType: iconUrl | null } — from RPC
const rpcFetchInProgress = {};    // { coinType: Promise } — dedup concurrent fetches

// ─── Supabase pre-load ────────────────────────────────────────────────────
/**
 * Call ONCE before enrichment with all coin types in the wallet.
 * Fetches all known decimals + icons from Supabase in a single query (~50ms).
 * Populates supabaseDecimalsCache so getDecimals() is instant for known coins.
 */
export async function initSupabaseDecimalsCache(coinTypes) {
  if (!coinTypes || coinTypes.length === 0) return;
  try {
    const data = await getDecimalsFromSupabase(coinTypes);
    Object.assign(supabaseDecimalsCache, data);
  } catch (err) {
    // Non-blocking — fall through to RPC
    console.warn('⚠️ Supabase decimals pre-load failed (non-blocking):', err.message);
  }
}

// ─── Background verify ────────────────────────────────────────────────────
/**
 * Call AFTER setUserObjects (tokens already displayed).
 * Verifies every coin type's decimals against RPC.
 * - Catches any wrong KNOWN_DECIMALS values (like TOILET was 9 should be 6)
 * - Writes correct values back to Supabase for fast future loads
 * - Returns a map of { coinType: decimals } for any corrections found
 *   so the caller can re-enrich affected objects if needed
 */
export async function verifyDecimalsInBackground(suiClient, coinTypes) {
  if (!suiClient || !coinTypes || coinTypes.length === 0) return {};
  const corrections = {};
  const toUpsert = [];

  await Promise.all(coinTypes.map(async (coinType) => {
    try {
      const meta = await suiClient.getCoinMetadata({ coinType });
      if (!meta) return;

      const rpcDecimals = meta.decimals ?? 9;
      const rpcIcon = meta.iconUrl || null;

      // Always write to RPC cache
      rpcDecimalsCache[coinType] = rpcDecimals;
      if (rpcIcon) rpcIconCache[coinType] = rpcIcon;

      // Check against what we used for display
      const addr = coinType.split('::')[0].toLowerCase();
      const usedDecimals = KNOWN_DECIMALS[addr]
        ?? supabaseDecimalsCache[coinType]?.decimals
        ?? rpcDecimalsCache[coinType]
        ?? 9;

      if (usedDecimals !== rpcDecimals) {
        console.warn(
          `⚠️ [CoinMetadata] Decimal mismatch for ${coinType.split('::').pop()}:`,
          `used ${usedDecimals}, RPC says ${rpcDecimals} — correcting`
        );
        corrections[coinType] = rpcDecimals;
        supabaseDecimalsCache[coinType] = { decimals: rpcDecimals, iconUrl: rpcIcon };
      }

      // Queue upsert for Supabase (verified = true)
      const cached = supabaseDecimalsCache[coinType];
      if (!cached?.rpcVerified) {
        toUpsert.push({
          coinType,
          decimals: rpcDecimals,
          symbol: extractSymbol(coinType),
          iconUrl: rpcIcon,
          rpcVerified: true,
        });
      }
    } catch {
      // RPC failed for this token — skip silently, don't break display
    }
  }));

  // Write all verified entries to Supabase in one batch
  if (toUpsert.length > 0) {
    upsertCoinMetadata(toUpsert).catch(() => {}); // fire-and-forget
  }

  return corrections; // caller can use this to re-enrich if needed
}

/**
 * Get decimals for a coin type.
 * Resolution order: KNOWN_DECIMALS → Supabase cache → RPC cache → RPC fetch → 9
 */
async function getDecimals(suiClient, coinType) {
  if (!coinType) return 9;

  // 1. Known table (instant)
  const addr = coinType.split('::')[0].toLowerCase();
  if (KNOWN_DECIMALS[addr] !== undefined) {
    return KNOWN_DECIMALS[addr];
  }

  // 2. Supabase cache (populated by initSupabaseDecimalsCache)
  if (supabaseDecimalsCache[coinType]?.decimals !== undefined) {
    return supabaseDecimalsCache[coinType].decimals;
  }

  // 3. RPC cache (populated by previous verify or fetch)
  if (rpcDecimalsCache[coinType] !== undefined) {
    return rpcDecimalsCache[coinType];
  }

  // 4. RPC fetch (new unknown token — deduplicated)
  if (!suiClient) return 9;

  if (!rpcFetchInProgress[coinType]) {
    rpcFetchInProgress[coinType] = suiClient.getCoinMetadata({ coinType })
      .then(meta => {
        const decimals = meta?.decimals ?? 9;
        rpcDecimalsCache[coinType] = decimals;
        rpcIconCache[coinType] = meta?.iconUrl || null;
        // Write to Supabase so it's fast next time
        upsertCoinMetadata([{
          coinType,
          decimals,
          symbol: extractSymbol(coinType),
          iconUrl: meta?.iconUrl || null,
          rpcVerified: true,
        }]).catch(() => {});
        delete rpcFetchInProgress[coinType];
        return decimals;
      })
      .catch(() => {
        rpcDecimalsCache[coinType] = 9;
        rpcIconCache[coinType] = null;
        delete rpcFetchInProgress[coinType];
        return 9;
      });
  }

  return rpcFetchInProgress[coinType];
}

/**
 * Extract coin type from object type string
 */
export function extractCoinType(objectType) {
  if (!objectType) return null;
  if (!objectType.includes('::coin::Coin')) return null;
  const match = objectType.match(/::coin::Coin<(.+?)>/);
  return match ? match[1] : null;
}

/**
 * Parse raw balance to human-readable amount
 */
export function parseBalance(rawBalance, decimals = 9) {
  if (!rawBalance) return '0';
  try {
    const balance = BigInt(rawBalance);
    const divisor = BigInt(10 ** decimals);
    const whole = balance / divisor;
    const fraction = balance % divisor;
    const fractionStr = fraction.toString().padStart(decimals, '0');
    const trimmedFraction = fractionStr.replace(/0+$/, '');
    if (trimmedFraction === '') return whole.toString();
    return `${whole}.${trimmedFraction}`;
  } catch (e) {
    return '0';
  }
}

/**
 * Extract human-readable symbol from coin type string.
 * No network calls — pure string parsing.
 */
export function extractSymbol(coinType) {
  if (!coinType) return 'Unknown';
  try {
    const lowerType = coinType.toLowerCase();

    if (coinType.includes('::lp::LP<') || coinType.includes('::LP<')) {
      return parseLPToken(coinType);
    }
    if (['staked', 'staking', 'stake', 'validator'].some(k => lowerType.includes(k))) {
      if (lowerType.includes('hasui')) return 'haSUI';
      if (lowerType.includes('vsui')) return 'vSUI';
      if (lowerType.includes('afsui')) return 'afSUI';
      if (lowerType.includes('sui')) return 'Staked SUI';
      return 'Staking Position';
    }
    if (coinType.includes('::position::Position') ||
        coinType.includes('::clmm::') ||
        coinType.includes('::concentrated_liquidity::')) {
      return 'DEX Position';
    }
    if (coinType.includes('::vault::') ||
        coinType.includes('::receipt::') ||
        coinType.includes('::farm::')) {
      return 'Vault Receipt';
    }
    if (coinType.includes('::lending::') ||
        coinType.includes('::borrow::') ||
        coinType.includes('::collateral::')) {
      return 'Lending Position';
    }

    const parts = coinType.split('::');
    if (parts.length >= 3) {
      let symbol = parts[2];
      if (symbol.includes('<')) symbol = symbol.split('<')[0];
      return symbol.trim().toUpperCase();
    }
    return 'Unknown';
  } catch (err) {
    return 'Unknown';
  }
}

function parseLPToken(coinType) {
  try {
    const match = coinType.match(/LP<([^,]+),\s*([^>]+)>/);
    if (match && match.length >= 3) {
      const symbolA = extractSymbol(match[1].trim());
      const symbolB = extractSymbol(match[2].trim());
      if (symbolA === 'Unknown' || symbolB === 'Unknown') return 'LP Token';
      return `${symbolA}-${symbolB} LP`;
    }
    return 'LP Token';
  } catch (err) {
    return 'LP Token';
  }
}

/**
 * Build metadata from coin type string — decimals resolved separately.
 */
function buildMetadataFromType(coinType, decimals = 9) {
  const symbol = extractSymbol(coinType);
  return {
    name: symbol,
    symbol,
    decimals,
    iconUrl: null,
    verified: false,
    fromCache: false,
  };
}

/**
 * Enrich a single object with metadata — NO RPC CALLS.
 */
export async function enrichObjectWithMetadata(_suiClient, obj) {
  if (!obj?.data) return obj;

  const objectType = obj.data.type;
  const coinType = extractCoinType(objectType);

  if (!coinType) {
    const displayName = obj.data.display?.data?.name;
    const typeParts = objectType.split('::');
    return {
      ...obj,
      enriched: {
        isCoin: false,
        displayName: displayName || typeParts[typeParts.length - 1] || 'Unknown Object',
        subtitle: typeParts[typeParts.length - 1] || 'Object',
        iconUrl: obj.data.display?.data?.image_url || null,
      }
    };
  }

  // Resolve decimals: known table → RPC cache → RPC fetch → fallback 9
  const decimals = await getDecimals(_suiClient, coinType);
  const metadata = buildMetadataFromType(coinType, decimals);
  const balance = obj.data.content?.fields?.balance || '0';

  return {
    ...obj,
    enriched: {
      isCoin: true,
      coinType,
      metadata,
      rawBalance: balance,
      formattedBalance: parseBalance(balance, decimals),
      displayName: metadata.name,
      symbol: metadata.symbol,
      iconUrl: getCachedIcon(coinType)
        || rpcIconCache[coinType]
        || supabaseDecimalsCache[coinType]?.iconUrl
        || KNOWN_ICONS[coinType.split('::')[0].toLowerCase()]
        || null,
      decimals,
    }
  };
}

/**
 * Pre-fetch decimals for all coin types in one pass.
 * 1. Load all from Supabase in one query (fast)
 * 2. RPC fetch only for tokens not in any cache
 */
export async function prefetchDecimals(suiClient, objects) {
  if (!objects?.length) return;

  // Collect all unique coin types in this wallet
  const allCoinTypes = new Set();
  objects.forEach(obj => {
    const objectType = obj?.data?.type;
    if (!objectType) return;
    const coinType = extractCoinType(objectType);
    if (coinType) allCoinTypes.add(coinType);
  });

  if (allCoinTypes.size === 0) return;

  // Step 1: Load from Supabase (all at once, fast)
  await initSupabaseDecimalsCache([...allCoinTypes]);

  // Step 2: RPC fetch only for coins not resolved by KNOWN_DECIMALS or Supabase
  if (!suiClient) return;
  const unknowns = [...allCoinTypes].filter(coinType => {
    const addr = coinType.split('::')[0].toLowerCase();
    if (KNOWN_DECIMALS[addr] !== undefined) return false;
    if (supabaseDecimalsCache[coinType]?.decimals !== undefined) return false;
    if (rpcDecimalsCache[coinType] !== undefined) return false;
    return true;
  });

  if (unknowns.length > 0) {
    await Promise.all(unknowns.map(ct => getDecimals(suiClient, ct)));
  }
}

/**
 * Enrich multiple objects — pre-fetch decimals first so enrichment is cache-only.
 */
export async function enrichObjectsWithMetadata(suiClient, objects) {
  if (!objects || objects.length === 0) return [];
  console.log(`📊 Enriching ${objects.length} objects...`);

  // Pre-populate decimals cache for all unknowns before enriching
  await prefetchDecimals(suiClient, objects);

  // Now enrich — all decimals are in cache, no mid-render RPC calls
  const enrichedObjects = await Promise.all(
    objects.map(obj => enrichObjectWithMetadata(suiClient, obj))
  );
  const coinCount = enrichedObjects.filter(obj => obj.enriched?.isCoin).length;
  console.log(`✅ Enriched ${coinCount}/${objects.length} coin objects`);
  return enrichedObjects;
}

/**
 * Get display info for an object
 */
export function getObjectDisplayInfo(obj) {
  if (obj.enriched) {
    if (obj.enriched.isCoin && obj.enriched.symbol) {
      return {
        name: obj.enriched.displayName,
        subtitle: `${obj.enriched.formattedBalance} ${obj.enriched.symbol}`,
        type: obj.enriched.coinType,
        iconUrl: obj.enriched.iconUrl || null,
        isEnriched: true,
      };
    } else {
      return {
        name: obj.enriched.displayName,
        subtitle: obj.enriched.subtitle || 'Object',
        type: obj.data?.type || 'unknown',
        iconUrl: obj.enriched.iconUrl || obj.data?.display?.data?.image_url || null,
        isEnriched: true,
      };
    }
  }

  const objName = obj?.data?.display?.data?.name
    || obj?.data?.type?.split?.('::')?.pop?.()
    || 'Unknown Object';
  const objType = obj?.data?.type?.split?.('::')?.slice?.(-2)?.join?.('::') || 'unknown';

  return {
    name: objName,
    subtitle: objType,
    type: objType,
    isEnriched: false,
  };
}

// Backward compatibility exports
export function clearMetadataCache() {
  Object.keys(rpcDecimalsCache).forEach(k => delete rpcDecimalsCache[k]);
}
export async function getCoinMetadata(suiClient, coinType) {
  const decimals = await getDecimals(suiClient, coinType);
  return buildMetadataFromType(coinType, decimals);
}
export async function fetchCoinMetadata(suiClient, coinType) {
  const decimals = await getDecimals(suiClient, coinType);
  return buildMetadataFromType(coinType, decimals);
}
